import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * 自帶的浮動提示。
 *
 * ⚠️ 刻意不用 rsuite 的 toaster（task-8905）：它的 `.rs-toast-container` 樣式在
 * `rsuite/toaster/styles` 裡，只 import `Message/styles` 時容器會是 position:static，
 * 於是每跳一次提示就把整頁往下推 60px、兩秒後又縮回去。使用者看不到提示，
 * 而且捲動位置一變，滑鼠底下的東西就換人 —— 按下去會落在別的元素上，
 * 症狀就是「點了沒反應」。這裡自己畫，position:fixed，絕不影響版面高度。
 */
const Layer = styled.div`
    position: fixed;
    top: 16px;
    left: 0;
    right: 0;
    z-index: 1200;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    /* 容器本身不吃點擊，只有提示本體可以被關掉 */
    pointer-events: none;
`;

const TONES = {
    success: { background: '#e8f5e9', color: '#1b5e20', border: '#a5d6a7' },
    error: { background: '#ffebee', color: '#b71c1c', border: '#ef9a9a' },
    warning: { background: '#fff3e0', color: '#e65100', border: '#ffcc80' },
    info: { background: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
};

const Toast = styled.div`
    pointer-events: auto;
    max-width: 560px;
    padding: 10px 16px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1.6;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
    background-color: ${(props) => (TONES[props.tone] || TONES.info).background};
    color: ${(props) => (TONES[props.tone] || TONES.info).color};
    border: 1px solid ${(props) => (TONES[props.tone] || TONES.info).border};
`;

const DURATION = 4000;

/** 回傳 [提示元素, push(type, text)]，兩個頁面共用 */
export const useToasts = () => {
    const [toasts, setToasts] = useState([]);
    const seq = useRef(0);
    const timers = useRef([]);

    const dismiss = useCallback((id) => {
        setToasts((current) => current.filter((t) => t.id !== id));
    }, []);

    const push = useCallback(
        (tone, text) => {
            const id = ++seq.current;
            setToasts((current) => current.concat([{ id, tone, text }]));
            timers.current.push(window.setTimeout(() => dismiss(id), DURATION));
        },
        [dismiss]
    );

    useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

    const element = (
        <Layer data-testid="toasts">
            {toasts.map((t) => (
                <Toast key={t.id} tone={t.tone} onClick={() => dismiss(t.id)} title="點一下關閉">
                    {t.text}
                </Toast>
            ))}
        </Layer>
    );

    return [element, push];
};

export default useToasts;
